import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, TreeSelect, Icon, Col, Row, Typography, Empty, Dropdown } from 'antd';
import { Link } from 'react-router-dom';
import { ChromePicker as ColorPicker } from 'react-color';
import arrayMove from 'array-move';
import { shuffle, get, isEmpty } from 'lodash';
import { pageUtils } from '../../../utils/common';

const { Text } = Typography;

const defaultValues = {
    colorWheel: shuffle(['#c41d7f', '#531dab', '#1d39c4', '#096dd9', '#08979c', '#389e0d', '#7cb305', '#d4b106', '#d48806', '#d46b08', '#d4380d', '#cf1322']),
    paths: ['']
};

const getTileColor = (colorWheelArr, index) => {
    index %= colorWheelArr.length;
    return colorWheelArr[index];
};

const Component = ({ paths, colorWheel, pages }) => {
    const pathPrefix =
        '/' +
        window.location.pathname
            .split('/')
            .splice(1, 1)
            .toString();

    const getTileSpan = () => {
        if (paths.length <= 4) {
            return 12; //minus 1 span due to margin.
        } else if (paths.length <= 6) {
            return 8; //minus 1 span due to margin.
        } else {
            return 6; //minus 1 span due to margin.
        }
    };

    const getPageInfo = path => {
        const pagePathStructure = pageUtils.getObjectStructureFromPath(path);
        const root = pageUtils.getRootFormPath(path).replace('/', '');
        const rootPages = pageUtils.objectifyPages(pages);

        const fullPathStructure = pagePathStructure === '' ? root : root + '.' + pagePathStructure;

        return get(rootPages, fullPathStructure);
    };

    const createTiles = data => {
        if (data.length === 0 || (data.length === 1 && isEmpty(data[0]))) {
            return <Empty description="No tiles created." />;
        }
        return data.map((path, index) => {
            const selectedPage = getPageInfo(path);

            if (!selectedPage) {
                console.log(`Cannot create tile for '${path}', has the page been deleted or move ?`);
                return (
                    <Col span={getTileSpan()} key={index} title={path} >
                        <div className="pageTile pageTile--invalid clickable" style={{ backgroundColor: getTileColor(colorWheel, index) }}>
                            <Icon type="warning" />
                            <div className="pageInfo">
                                <Text className="pageTile-name">{path}</Text>
                                <Text className="pageTile-description" type="secondary">
                                    - Invalid link -
                                </Text>
                            </div>
                        </div>
                        
                    </Col>
                );
            } else {
                const linkTo = pathPrefix === '/edit' || pathPrefix === '/view' ? pathPrefix + selectedPage.path : selectedPage.path;
                return (
                        <Col span={getTileSpan()} title={selectedPage.title + ' - ' + selectedPage.description}>
                            <Link to={linkTo} key={index}>
                                <div  className="pageTile clickable" style={{ backgroundColor: getTileColor(colorWheel, index) }}>
                                    <Icon type="select" />
                                    <div className="pageInfo">
                                        <Text className="pageTile-name">{selectedPage.title}</Text>
                                        <Text className="pageTile-description" type="secondary">
                                            {selectedPage.description}
                                        </Text>
                                    </div>
                                </div>
                            </Link>
                        </Col>
                );
            }
        });
    };
        return <Row gutter={10} className="pageTiles">{createTiles(paths)}</Row>;
};
Component.defaultProps = {
    paths: defaultValues.paths,
    colorWheel: defaultValues.colorWheel
};

const EditForm = props => {
    const { getFieldDecorator, getFieldValue } = props.form;
    const [colorWheel, setColorwheel] = useState(props.colorWheel);
    const [paths, setPaths] = useState(props.paths);

    props.form.mapValuesToProps = values => {
        values.colorWheel = colorWheel;
        return values;
    };

    useEffect(() => {
        props.form.validateFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ~~~~ Event-handlers ~~~~ */
    const handleColorChanged = (newColor, index) => {
        let newColorWheel = [...colorWheel];
        newColorWheel[index] = newColor.hex;
        setColorwheel(newColorWheel);
    };
    const handleShuffelColorwheel = () => {
        setColorwheel(shuffle(colorWheel));
    };
    const handleAddPage = e => {
        let newTilePath = paths.concat('');
        setPaths(newTilePath);
    };
    const handleRemovePage = index => {
        let pathsFields = getFieldValue('paths');
        pathsFields.splice(index, 1);
        props.form.resetFields();

        setPaths(pathsFields);
    };
    const handleMovePageDown = index => {
        let pathsFields = getFieldValue('paths');
        if (index + 1 < pathsFields.length) {
            props.form.resetFields();
            setPaths(arrayMove(pathsFields, index, ++index));
        }
    };
    const handleMovePageUp = index => {
        if (index > 0) {
            let pathsFields = getFieldValue('paths');
            props.form.resetFields();
            setPaths(arrayMove(pathsFields, index, --index));
        }
    };

    /* ~~~~ Util-methods ~~~~ */
    const validatePagePath = (rule, value, callback) => {
        if (value && value.trim().length > 0) {
            const objectStruct = pageUtils.getObjectStructureFromPath(value);
            const rootPages = pageUtils.objectifyPages(props.pages);
            const rootPage = pageUtils.getRootFormPath(value).slice(1);

            if ((objectStruct === '' && rootPages[rootPage] !== undefined) || get(rootPages[rootPage], objectStruct) !== undefined) {
                callback();
                return;
            } else {
                callback("This page deosn't exist, perhaps it has been moved, renamed or deleted ?");
                return;
            }
        } else {
            callback('A page path for this tile is required.');
        }
    };
    const convertPageToTreeData = page => {
        let treeData = { title: page.title, value: page.path, key: page.path, children: [] };
        if (!isEmpty(page.subpages)) {
            for (let subpage in page.subpages) {
                treeData.children.push(convertPageToTreeData(page.subpages[subpage]));
            }
        }
        return treeData;
    };
    const createFormItems = pageTiles => {
        return pageTiles.map((entry, index) => (
            <div className="pageTilesForm-item" key={index}>
                <Row type="flex" justify="space-between">
                    <Col span={5}>
                        <Text type="secondary">
                            Color{' '}
                            <Dropdown overlay={<ColorPicker color={colorWheel[index]} onChangeComplete={color => handleColorChanged(color, index)} />} trigger={['click']}>
                                <span className="pageTitleForm-item-colorBullet clickable" style={{ backgroundColor: getTileColor(colorWheel, index) }} />
                            </Dropdown>
                        </Text>
                    </Col>
                    <Col>
                        <div className="pageTilesForm-actions">
                            {pageTiles.length === index + 1 ? <Icon type="plus-circle-o" className="clickable" title="Add new page tile" onClick={handleAddPage} /> : null}
                            {pageTiles.length > 1 ? (
                                <React.Fragment>
                                    <Icon type="minus-circle-o" className="clickable" title="Remove page tile" onClick={handleRemovePage.bind(this, index)} />
                                    <Icon type="up-circle" className="clickable" title="Move page tile up" onClick={handleMovePageUp.bind(this, index)} />
                                    <Icon type="down-circle" className="clickable" title="Move page tile down" onClick={handleMovePageDown.bind(this, index)} />
                                </React.Fragment>
                            ) : null}
                        </div>
                    </Col>
                </Row>
                <Form.Item required hasFeedback>
                    {getFieldDecorator(`paths[${index}]`, {
                        rules: [
                            {
                                validator: validatePagePath
                            }
                        ],
                        initialValue: entry
                    })(<TreeSelect dropdownStyle={{ maxHeight: 400, overflow: 'auto' }} treeData={props.pages.map(convertPageToTreeData)} placeholder="Please select a page" treeDefaultExpandAll />)}
                </Form.Item>
            </div>
        ));
    };

    return (
        <React.Fragment>
            <Row type="flex" justify="space-between">
                <Col span={5}>
                    <label className="ant-form-item-required" title="Pages" style={{ marginBottom: '1rem', display: 'block' }}>
                        Pages
                    </label>
                </Col>
                <Col>
                    <Icon type="redo" className="clickable" title="Shuffel colors" onClick={handleShuffelColorwheel} style={{ fontSize: '1.5em' }} />
                </Col>
            </Row>

            {createFormItems(paths)}
        </React.Fragment>
    );
};
EditForm.defaultProps = {
    paths: defaultValues.paths,
    colorWheel: defaultValues.colorWheel
};

const mapEditFormStateToProps = state => ({
    pages: state.pages.pages
});

const mapComponentStateToProps = state => ({
    pages: state.pages.pages
});

const exportedComponent = connect(
    mapComponentStateToProps,
    null
)(Component);

const exportedEditForm = connect(
    mapEditFormStateToProps,
    null
)(EditForm);

export { exportedComponent as Component, exportedEditForm as EditForm };
